// Stealth-Agent FE — Work Summary. Client-facing daily intelligence with no raw
// report table. Uses aggregated KPIs, app mix visualization, and AI narrative.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, DatePicker, Button, Empty, Segmented, Pagination } from 'antd';
import {
  Calendar, Zap, RefreshCw, Clock, Sparkles, Activity, Coffee, Download, Trophy, Gauge, Medal, Flame, Star, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis } from 'recharts';
import dayjs from 'dayjs';
import { getDevices, getReports, WORK_SUMMARY_STREAM } from '../api/client';
import { useAuth } from '../../../contexts/AuthContext';
import { useStealthAiStream, mdToHtml } from './useStealthAiStream';
import { fmtDuration, relativeTime, isOnline, deviceDisplayName, deviceSelectLabel, fmtTime } from './utils';
import HourlyInsights from './HourlyInsights';
import {
  Card, Kpi, PageHeader, SectionTitle, Reveal, RevealGroup, Skeleton, EASE, StatusPill, downloadReportPDF, CHART_PALETTE,
} from './ui';

function ChartTip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  return (
    <div className="rounded-lg bg-gray-900 text-white text-xs px-3 py-2 shadow-xl">
      <div className="font-medium">{p.name}</div>
      <div className="text-gray-300">{fmtDuration(p.value)}</div>
    </div>
  );
}

export default function WorkSummary() {
  const { user } = useAuth() || {};
  const [devices, setDevices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [date, setDate] = useState(dayjs());
  const [reports, setReports] = useState([]);
  const [chartReports, setChartReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('daily'); // 'daily' | 'hourly'
  const [total, setTotal] = useState(0);
  const [overall, setOverall] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const ai = useStealthAiStream(WORK_SUMMARY_STREAM);
  const isMainAdminView = Boolean(user && (user.role === 'main_admin' || user.isMainAdminSwitch));

  const fetchDevices = useCallback(async () => {
    setDevicesLoading(true);
    try {
      const d = await getDevices();
      setDevices(d.devices || []);
      setSelected((cur) => cur || (d.devices && d.devices[0] && d.devices[0].device_id) || null);
    } catch { /* noop */ } finally { setDevicesLoading(false); }
  }, []);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  // Viewer's browser timezone so "per day" respects the current admin's calendar day.
  const TZ = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata'; } catch { return 'Asia/Kolkata'; } })();

  useEffect(() => {
    setPage(1);
    ai.reset();
    if (selected) loadChartReports(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, date]);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const d = await getReports({
          device_id: selected,
          limit: pageSize,
          skip: (page - 1) * pageSize,
          date: date.format('YYYY-MM-DD'),
          tz: TZ,
          totals: 1,
        });
        if (cancelled) return;
        setReports(d.reports || []);
        setTotal(d.total || 0);
        setOverall(d.totals || null);
      } catch {
        if (cancelled) return;
        setReports([]);
        setTotal(0);
        setOverall(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, date, page, pageSize]);

  async function loadReports(deviceId, pageNum = page, size = pageSize) {
    if (!deviceId) return;
    setLoading(true);
    try {
      const d = await getReports({
        device_id: deviceId,
        limit: size,
        skip: (pageNum - 1) * size,
        date: date.format('YYYY-MM-DD'),
        tz: TZ,
        totals: 1,
      });
      setReports(d.reports || []);
      setTotal(d.total || 0);
      setOverall(d.totals || null);
    } catch {
      setReports([]);
      setTotal(0);
      setOverall(null);
    } finally {
      setLoading(false);
    }
  }

  // Charts need a day sample (API max 100); table uses its own paginated fetch.
  async function loadChartReports(deviceId) {
    if (!deviceId) return;
    try {
      const d = await getReports({
        device_id: deviceId,
        limit: 100,
        skip: 0,
        date: date.format('YYYY-MM-DD'),
        tz: TZ,
        totals: 0,
      });
      setChartReports(d.reports || []);
    } catch {
      setChartReports([]);
    }
  }

  const totals = useMemo(() => {
    if (overall) {
      return {
        active: overall.active || 0,
        idle: overall.idle || 0,
        worked: overall.worked || 0,
        keys: overall.keys || 0,
        clicks: overall.clicks || 0,
        pct: overall.active_pct || 0,
      };
    }
    let active = 0; let idle = 0; let keys = 0; let clicks = 0;
    chartReports.forEach((r) => {
      active += r.time_active_sec || 0;
      idle += r.time_idle_sec || 0;
      keys += r.keyboard_key_presses || 0;
      clicks += r.mouse_clicks || 0;
    });
    const worked = active + idle;
    return { active, idle, worked, keys, clicks, pct: worked ? Math.round((active / worked) * 100) : 0 };
  }, [overall, chartReports]);

  const appMix = useMemo(() => {
    const appTime = {};
    chartReports.forEach((r) => {
      if (!r.active_app) return;
      appTime[r.active_app] = (appTime[r.active_app] || 0) + (r.time_active_sec || 0);
    });
    return Object.entries(appTime)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, value]) => ({ name, value }));
  }, [chartReports]);

  const intensityBands = useMemo(() => {
    const slots = [
      { name: 'Morning', value: 0 },
      { name: 'Midday', value: 0 },
      { name: 'Afternoon', value: 0 },
      { name: 'Evening', value: 0 },
    ];
    chartReports.forEach((r) => {
      const dt = new Date(r.createdAt || r.timestamp || Date.now());
      const h = Number.isNaN(dt.getHours()) ? 12 : dt.getHours();
      const effort = (r.time_active_sec || 0) + Math.floor((r.keyboard_key_presses || 0) / 20);
      if (h < 12) slots[0].value += effort;
      else if (h < 15) slots[1].value += effort;
      else if (h < 19) slots[2].value += effort;
      else slots[3].value += effort;
    });
    return slots;
  }, [chartReports]);

  const engagementLabel = totals.pct >= 75
    ? 'Elite focus day'
    : totals.pct >= 60
      ? 'Strong execution'
      : totals.pct >= 45
        ? 'Balanced day'
        : 'Needs alignment';
  const peakBand = useMemo(
    () => [...intensityBands].sort((a, b) => b.value - a.value)[0]?.name || '—',
    [intensityBands]
  );
  const momentumScore = Math.max(0, Math.min(100, Math.round((totals.pct * 0.7) + (Math.min(total, 180) / 180) * 30)));
  const consistencyScore = Math.max(0, Math.min(100, Math.round((intensityBands.filter((b) => b.value > 0).length / 4) * 100)));
  const rankTier = momentumScore >= 85 ? 'Platinum' : momentumScore >= 70 ? 'Gold' : momentumScore >= 55 ? 'Silver' : 'Bronze';
  const achievementChips = useMemo(() => {
    const chips = [];
    if (totals.pct >= 75) chips.push({ label: 'Deep Focus', icon: Flame });
    if ((totals.keys || 0) >= 1000) chips.push({ label: 'High Output', icon: Star });
    if (consistencyScore >= 75) chips.push({ label: 'Steady Flow', icon: Medal });
    if (chips.length === 0) chips.push({ label: 'Momentum Building', icon: Star });
    return chips;
  }, [totals.pct, totals.keys, consistencyScore]);
  const aiGamified = useMemo(() => {
    if (!ai.text) {
      return { highlights: [], wins: 0, watchouts: 0 };
    }

    const lines = String(ai.text)
      .replace(/\r/g, '')
      .split('\n')
      .map((l) => l.replace(/\*\*/g, '').trim())
      .filter(Boolean);

    const bullets = lines
      .filter((l) => /^[-*•]\s+/.test(l))
      .map((l) => l.replace(/^[-*•]\s+/, '').trim())
      .filter((l) => l.length > 12);

    const fallback = lines
      .filter((l) => !/^section\s*\d+/i.test(l) && l.length > 30 && !/^\d+[.)]/.test(l))
      .slice(0, 4);

    const highlights = (bullets.length ? bullets : fallback).slice(0, 4);
    const positiveRegex = /(completed|implemented|resolved|improved|delivered|optimized|finished|success)/i;
    const riskRegex = /(issue|risk|blocked|delay|pending|bottleneck|error|concern)/i;
    const wins = highlights.filter((h) => positiveRegex.test(h)).length;
    const watchouts = highlights.filter((h) => riskRegex.test(h)).length;

    return { highlights, wins, watchouts };
  }, [ai.text]);

  const generate = () => {
    if (!selected) return;
    ai.run({ device_id: selected, startDate: date.format('YYYY-MM-DD'), endDate: date.format('YYYY-MM-DD') });
  };

  async function refreshAll() {
    await fetchDevices();
    if (selected) {
      await Promise.all([
        loadReports(selected, page, pageSize),
        loadChartReports(selected),
      ]);
    }
  }

  const dev = devices.find((d) => d.device_id === selected);
  const deviceOptions = devices.map((d) => ({
    value: d.device_id,
    label: deviceSelectLabel(d),
  }));

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-[1400px] mx-auto">
      <PageHeader
        icon={Calendar}
        title="Stealth Work Summary"
        subtitle="Executive-ready daily intelligence with premium visual storytelling."
        badge="Advanced"
        actions={
          <Button icon={<RefreshCw size={14} />} onClick={refreshAll} loading={devicesLoading || loading}>
            Refresh
          </Button>
        }
      />

      <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, ease: EASE }}>
        <Segmented
          size="large"
          value={view}
          onChange={setView}
          options={[
            { label: (<span className="flex items-center gap-1.5 px-1"><Calendar size={14} /> Daily Summary</span>), value: 'daily' },
            { label: (<span className="flex items-center gap-1.5 px-1"><Clock size={14} /> Hourly Insights</span>), value: 'hourly' },
          ]}
        />
      </motion.div>

      <AnimatePresence mode="wait">
        {view === 'hourly' ? (
          <motion.div key="hourly" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ ease: EASE, duration: 0.3 }}>
            <HourlyInsights />
          </motion.div>
        ) : (
          <motion.div key="daily" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ ease: EASE, duration: 0.3 }} className="space-y-6">
            <Card className="p-3.5 flex items-center gap-3 flex-wrap">
              <Select showSearch optionFilterProp="label" style={{ minWidth: 320 }}
                placeholder="Select device" value={selected} onChange={setSelected} options={deviceOptions} />
              <DatePicker value={date} onChange={(d) => setDate(d || dayjs())} allowClear={false} />
              <Button type="primary" icon={<Zap size={15} />} loading={ai.streaming} onClick={generate} disabled={!selected}
                style={{ background: 'linear-gradient(135deg,#0f766e,#14b8a6)', border: 'none', fontWeight: 600 }}>
                Generate summary
              </Button>
              {dev && (
                <div className="ml-auto">
                  <StatusPill online={isOnline(dev)} lastSeen={relativeTime(dev.last_heartbeat_at || dev.last_seen_at)} interval={dev.report_interval} />
                </div>
              )}
            </Card>

            <RevealGroup className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi icon={Gauge} label="Focus score" accent="emerald" value={`${totals.pct}%`} sub={engagementLabel} />
              <Kpi icon={Clock} label="Worked" accent="indigo" value={fmtDuration(totals.worked)} sub={`${date.format('MMM D')} performance`} />
              <Kpi icon={Activity} label="Active time" accent="sky" value={fmtDuration(totals.active)} sub={`${totals.pct}% of worked`} />
              <Kpi icon={Coffee} label="Idle time" accent="amber" value={fmtDuration(totals.idle)} sub={`${(totals.keys || 0).toLocaleString()} keys · ${(totals.clicks || 0).toLocaleString()} clicks`} />
            </RevealGroup>

            <Reveal>
              <Card className="p-6 relative overflow-hidden">
                <div className="absolute -top-28 -right-24 w-72 h-72 rounded-full bg-gradient-to-br from-teal-500/12 to-cyan-500/10 blur-3xl pointer-events-none" />
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                  <div className="xl:col-span-2">
                    <SectionTitle icon={Sparkles} right={
                      ai.text && !ai.streaming ? (
                        <Button size="small" icon={<Download size={13} />} onClick={() => downloadReportPDF({
                          title: 'Stealth Work Summary',
                          subtitle: `${dev ? deviceDisplayName(dev) : 'Device'} · ${date.format('MMMM D, YYYY')}`,
                          bodyHtml: mdToHtml(ai.text),
                          meta: { Date: date.format('YYYY-MM-DD'), Worked: fmtDuration(totals.worked), 'Focus score': `${totals.pct}%` },
                        })}>Download PDF</Button>
                      ) : null
                    }>
                      <span className="bg-gradient-to-r from-teal-600 to-cyan-600 dark:from-teal-300 dark:to-cyan-300 bg-clip-text text-transparent">AI Executive Brief</span>
                    </SectionTitle>
                    <div className="xl:h-[540px]">
                      <AnimatePresence mode="wait">
                        {ai.streaming && !ai.text && (
                          <motion.div key="l" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-2.5">
                            <Skeleton className="h-3.5 w-3/4" /><Skeleton className="h-3.5 w-full" /><Skeleton className="h-3.5 w-5/6" />
                            <div className="flex items-center gap-2 text-xs text-teal-500 pt-1">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" />
                              Building polished client summary...
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                      {ai.error && <div className="text-sm text-amber-600">{ai.error}</div>}
                      {!ai.text && !ai.streaming && !ai.error && (
                        <div className="h-full min-h-[260px] rounded-2xl border border-dashed border-gray-200 dark:border-white/[0.12]
                          bg-gray-50/70 dark:bg-white/[0.02] flex flex-col items-center justify-center text-center px-6">
                          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-500/15 to-cyan-500/10 flex items-center justify-center mb-3">
                            <Sparkles size={20} className="text-teal-500" />
                          </div>
                          <div className="text-[15px] font-semibold text-gray-800 dark:text-gray-100">AI Executive Brief is ready to generate</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md">
                            Pick a team member device and date, then click <span className="font-medium text-teal-600 dark:text-teal-300">Generate summary</span> to get a polished brief with highlights, wins, and performance signals.
                          </div>
                          <div className="mt-4 text-xs text-gray-400 dark:text-gray-500">
                            Tip: choose a date with tracked work for richer insights.
                          </div>
                        </div>
                      )}
                      {ai.text && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
                          className="h-full overflow-y-auto pr-2 text-[14px] text-gray-700 dark:text-gray-200 leading-[1.7]
                            [&::-webkit-scrollbar]:w-2
                            [&::-webkit-scrollbar-track]:bg-transparent
                            [&::-webkit-scrollbar-thumb]:bg-gray-300
                            dark:[&::-webkit-scrollbar-thumb]:bg-white/20
                            [&::-webkit-scrollbar-thumb]:rounded-full"
                          dangerouslySetInnerHTML={{ __html: mdToHtml(ai.text) }} />
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="rounded-2xl border border-gray-100 dark:border-white/[0.08] p-4 bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-500/[0.08] dark:to-cyan-500/[0.08]">
                      <div className="text-xs uppercase tracking-wider text-gray-500 mb-3">Performance Arena</div>
                      <div className="flex items-center gap-4">
                        <div
                          className="w-20 h-20 rounded-full grid place-items-center text-sm font-semibold text-teal-700 dark:text-teal-300"
                          style={{ background: `conic-gradient(#0f766e ${momentumScore}%, rgba(148,163,184,0.25) 0%)` }}
                        >
                          <span className="w-14 h-14 rounded-full bg-white dark:bg-gray-900 grid place-items-center">{momentumScore}</span>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Momentum score</div>
                          <div className="text-xl font-bold text-gray-900 dark:text-white">{momentumScore}/100</div>
                          <div className="text-xs text-teal-700 dark:text-teal-300">{rankTier} tier performance</div>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-gray-100 dark:border-white/[0.08] p-4">
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>Consistency</span>
                        <span className="tabular-nums">{consistencyScore}%</span>
                      </div>
                      <div className="mt-2 h-2 rounded-full bg-gray-100 dark:bg-white/[0.08] overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${consistencyScore}%` }}
                          transition={{ duration: 0.7, ease: 'easeOut' }}
                          className="h-full bg-gradient-to-r from-cyan-500 to-teal-500"
                        />
                      </div>
                      <div className="mt-3 text-xs text-gray-500">Peak window: <span className="font-medium text-gray-700 dark:text-gray-200">{peakBand}</span></div>
                    </div>

                    <div className="rounded-2xl border border-gray-100 dark:border-white/[0.08] p-4">
                      <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">Achievements</div>
                      <div className="flex flex-wrap gap-2">
                        {achievementChips.map((chip) => {
                          const Icon = chip.icon;
                          return (
                            <span key={chip.label} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-teal-100 text-teal-800 dark:bg-teal-500/20 dark:text-teal-200">
                              <Icon size={12} />
                              {chip.label}
                            </span>
                          );
                        })}
                        {aiGamified.wins > 0 && (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200">
                            <CheckCircle2 size={12} />
                            {aiGamified.wins} AI wins
                          </span>
                        )}
                        {aiGamified.watchouts > 0 && (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200">
                            <AlertTriangle size={12} />
                            {aiGamified.watchouts} watchouts
                          </span>
                        )}
                      </div>
                    </div>

                    {aiGamified.highlights.length > 0 && (
                      <div className="rounded-2xl border border-gray-100 dark:border-white/[0.08] p-4">
                        <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">AI Missions</div>
                        <div className="space-y-2 max-h-[230px] overflow-y-auto pr-1
                          [&::-webkit-scrollbar]:w-2
                          [&::-webkit-scrollbar-track]:bg-transparent
                          [&::-webkit-scrollbar-thumb]:bg-gray-300
                          dark:[&::-webkit-scrollbar-thumb]:bg-white/20
                          [&::-webkit-scrollbar-thumb]:rounded-full">
                          {aiGamified.highlights.map((item, idx) => (
                            <div key={`${idx}-${item.slice(0, 24)}`} className="rounded-xl border border-gray-100 dark:border-white/[0.06] px-3 py-2 bg-gray-50/70 dark:bg-white/[0.02]">
                              <div className="text-[11px] text-teal-600 dark:text-teal-300 font-medium mb-1">Mission {idx + 1}</div>
                              <div className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed line-clamp-2">{item}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </Reveal>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
              <Reveal className="lg:col-span-2">
                <Card className="p-5 h-full">
                  <SectionTitle icon={Trophy}>Top app mix</SectionTitle>
                  {loading ? <Skeleton className="h-[240px] w-full" /> : appMix.length === 0 ? <Empty description="No app activity yet" /> : (
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie data={appMix} dataKey="value" nameKey="name" innerRadius={58} outerRadius={90} paddingAngle={2}>
                          {appMix.map((_, i) => <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />)}
                        </Pie>
                        <Tooltip content={<ChartTip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                  {appMix.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      {appMix.slice(0, 4).map((a, i) => (
                        <div key={a.name} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: CHART_PALETTE[i % CHART_PALETTE.length] }} />
                            <span className="truncate text-gray-600 dark:text-gray-300">{a.name}</span>
                          </div>
                          <span className="text-gray-400 tabular-nums">{fmtDuration(a.value)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </Reveal>

              <Reveal className="lg:col-span-3">
                <Card className="p-5 h-full">
                  <SectionTitle icon={Activity}>Daily momentum</SectionTitle>
                  {loading ? <Skeleton className="h-[240px] w-full" /> : intensityBands.every((s) => s.value === 0) ? <Empty description="Not enough activity for timeline yet" /> : (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={intensityBands}>
                        <XAxis dataKey="name" tickLine={false} axisLine={false} />
                        <YAxis hide />
                        <Tooltip content={<ChartTip />} />
                        <Bar dataKey="value" radius={[10, 10, 4, 4]}>
                          {intensityBands.map((_, i) => <Cell key={i} fill={CHART_PALETTE[(i + 1) % CHART_PALETTE.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div className="rounded-xl border border-gray-100 dark:border-white/[0.06] p-3">
                      <div className="text-[11px] uppercase tracking-wider text-gray-400">Activity pulses</div>
                      <div className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">{total.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">signals analyzed for this day</div>
                    </div>
                    <div className="rounded-xl border border-gray-100 dark:border-white/[0.06] p-3">
                      <div className="text-[11px] uppercase tracking-wider text-gray-400">Top performer window</div>
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">{peakBand}</div>
                      <div className="text-xs text-gray-500">highest engagement block</div>
                    </div>
                  </div>
                </Card>
              </Reveal>
            </div>

            {isMainAdminView && (
              <Reveal>
                <Card className="p-5">
                  <SectionTitle icon={Activity} right={
                    <span className="text-xs text-gray-400 tabular-nums">
                      {total > 0
                        ? `${Math.min((page - 1) * pageSize + 1, total)}–${Math.min(page * pageSize, total)} of ${total.toLocaleString()}`
                        : '0 records'}
                    </span>
                  }>
                    Raw reports
                  </SectionTitle>
                  {loading ? (
                    <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
                  ) : reports.length === 0 ? (
                    <Empty description="No raw reports for this device/day." />
                  ) : (
                    <>
                      <div className="overflow-auto max-h-[320px] rounded-xl border border-gray-100 dark:border-white/[0.06]">
                        <table className="w-full text-sm">
                          <thead className="text-[11px] uppercase tracking-wide text-gray-400 sticky top-0 bg-white dark:bg-[#0f1115]">
                            <tr className="text-left">
                              <th className="py-2.5 px-3 font-medium">Time</th>
                              <th className="font-medium">App / Title</th>
                              <th className="text-right font-medium">Active</th>
                              <th className="text-right font-medium">Idle</th>
                              <th className="text-right font-medium">Keys</th>
                              <th className="text-right font-medium">Clicks</th>
                              <th className="text-right px-3 font-medium">OCR Words</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reports.map((r, i) => (
                              <motion.tr key={r._id || `${r.createdAt}-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.015, 0.2) }}
                                className="border-t border-gray-50 dark:border-white/[0.04] hover:bg-gray-50/70 dark:hover:bg-white/[0.02]">
                                <td className="py-2.5 px-3 text-xs text-gray-500 whitespace-nowrap tabular-nums">{fmtTime(r.createdAt || r.timestamp)}</td>
                                <td className="pr-3">
                                  <div className="font-medium text-gray-800 dark:text-gray-200">{r.active_app || '—'}</div>
                                  <div className="text-xs text-gray-400 truncate max-w-[380px]">{r.window_title || 'No title'}</div>
                                </td>
                                <td className="text-right text-gray-600 dark:text-gray-300 tabular-nums">{fmtDuration(r.time_active_sec || 0)}</td>
                                <td className="text-right text-gray-600 dark:text-gray-300 tabular-nums">{fmtDuration(r.time_idle_sec || 0)}</td>
                                <td className="text-right text-gray-600 dark:text-gray-300 tabular-nums">{(r.keyboard_key_presses || 0).toLocaleString()}</td>
                                <td className="text-right text-gray-600 dark:text-gray-300 tabular-nums">{(r.mouse_clicks || 0).toLocaleString()}</td>
                                <td className="text-right px-3 text-gray-600 dark:text-gray-300 tabular-nums">
                                  {r.ocr_word_count != null ? r.ocr_word_count.toLocaleString() : <span className="text-gray-300 dark:text-gray-600">—</span>}
                                </td>
                              </motion.tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="mt-3 flex justify-end">
                        <Pagination
                          size="small"
                          current={page}
                          pageSize={pageSize}
                          total={total}
                          showSizeChanger
                          pageSizeOptions={['10', '20', '50', '100']}
                          showTotal={(t, range) => `${range[0]}–${range[1]} of ${t}`}
                          onChange={(p, ps) => {
                            setPage(p);
                            setPageSize(ps);
                          }}
                        />
                      </div>
                    </>
                  )}
                </Card>
              </Reveal>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
